/**
 * AppleMusicService - lightweight search via iTunes Search API (no auth)
 * API docs: https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/iTuneSearchAPI/index.html
 */

const APPLE_SEARCH_URL = 'https://itunes.apple.com/search';

const inferRecordingType = (collectionName = '') => {
	const lower = collectionName.toLowerCase();
	if (lower.includes('cast')) return 'Cast Recording';
	if (lower.includes('soundtrack') || lower.includes('motion picture')) return 'Soundtrack';
	return 'Album';
};

const buildTerm = (title = '', artist = '') => {
	if (artist) return `${title} ${artist}`;
	return title;
};

export const searchAppleMusicOptions = async (title, artist = '', limit = 10) => {
	try {
		const term = buildTerm(title, artist);

		const fetchEntity = async (entity, attribute, termValue) => {
			const url = `${APPLE_SEARCH_URL}?term=${encodeURIComponent(termValue)}&media=music&entity=${entity}&limit=${limit}${attribute ? `&attribute=${attribute}` : ''}`;
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Apple Music API error: ${response.status}`);
			}
			const data = await response.json();
			return data.results || [];
		};

		// Try track-first then albums with full term (title + artist)
		const [trackResults, albumResults] = await Promise.all([
			fetchEntity('musicTrack', 'songTerm', term),
			fetchEntity('album', null, term),
		]);

		let combined = [...trackResults, ...albumResults];

		// If nothing returned and artist provided, retry with title-only to be forgiving
		if (combined.length === 0 && artist) {
			const titleOnlyTerm = buildTerm(title, '');
			const [trackRetry, albumRetry] = await Promise.all([
				fetchEntity('musicTrack', 'songTerm', titleOnlyTerm),
				fetchEntity('album', null, titleOnlyTerm),
			]);
			combined = [...trackRetry, ...albumRetry];
		}

		if (!combined.length) return [];

		// Normalize to our metadata shape
		const seen = new Set();
		const normalized = [];

		const pushIfNew = (item, isTrack) => {
			const key = `${item.collectionId || ''}-${item.trackId || ''}-${item.artistName || ''}`;
			if (seen.has(key)) return;
			seen.add(key);
			normalized.push({
				title: isTrack ? item.trackName || title : item.collectionName || title,
				artist: item.artistName || artist,
				album: item.collectionName || item.trackName || '',
				recordingType: inferRecordingType(item.collectionName || ''),
				releaseYear: item.releaseDate ? new Date(item.releaseDate).getFullYear() : null,
				genres: item.primaryGenreName ? [item.primaryGenreName] : [],
				previewUrl: item.previewUrl || '',
				artworkUrl: item.artworkUrl100 || '',
				source: 'apple',
				musicbrainzId: null,
				recordingId: isTrack ? `apple-track-${item.trackId || ''}` : `apple-album-${item.collectionId || ''}`,
				lyricists: [],
				arrangers: [],
				composers: [],
				isMusical: (item.collectionName || '').toLowerCase().includes('musical') || false,
				musicalName: '',
				yearWritten: null,
				releaseLabel: item.collectionExplicitness || '',
				releaseDisambiguation: item.country || '',
			});
		};

		trackResults.forEach((item) => pushIfNew(item, true));
		albumResults.forEach((item) => pushIfNew(item, false));
		return normalized;
	} catch (error) {
		console.error('Error fetching Apple Music options:', error);
		return [];
	}
};

// Simple rate limit (1 request/sec)
let lastRequestTime = 0;
const RATE_LIMIT_MS = 1000;

const rateLimit = () => {
	return new Promise((resolve) => {
		const now = Date.now();
		const elapsed = now - lastRequestTime;
		if (elapsed < RATE_LIMIT_MS) {
			setTimeout(() => {
				lastRequestTime = Date.now();
				resolve();
			}, RATE_LIMIT_MS - elapsed);
		} else {
			lastRequestTime = now;
			resolve();
		}
	});
};

export const searchAppleMusicOptionsWithRateLimit = async (...args) => {
	await rateLimit();
	return searchAppleMusicOptions(...args);
};

export default {
	searchAppleMusicOptions,
	searchAppleMusicOptionsWithRateLimit,
};
